import React, { useEffect, useState, useCallback } from "react";
import { STATIONS } from "../config/stations";
import { History, Music2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const HistoryColumn = ({ station }) => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/now-playing/${station.id}/history?limit=5`);
      if (res.ok) setTracks(await res.json());
    } finally {
      setLoading(false);
    }
  }, [station.id]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  return (
    <div
      data-testid={`history-${station.id}`}
      className="bg-jet-surface border border-gold/20 rounded-lg overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-900">
        <div className="flex items-center gap-2">
          <Music2 className="w-4 h-4 text-gold" />
          <div>
            <div className="font-condensed uppercase tracking-[0.3em] text-[10px] text-gold">
              Recently on
            </div>
            <div className="font-display text-lg leading-none text-white uppercase">
              {station.name}
            </div>
          </div>
        </div>
      </div>
      {loading && tracks.length === 0 ? (
        <div className="px-4 py-8 text-neutral-500 text-sm text-center">Loading…</div>
      ) : tracks.length === 0 ? (
        <div className="px-4 py-8 text-neutral-500 text-sm text-center">
          {station.kind === "video"
            ? "Live broadcast — no track history."
            : "No history yet. Tune in and it'll fill up."}
        </div>
      ) : (
        <ul className="divide-y divide-neutral-900">
          {tracks.map((t, i) => (
            <li
              key={t.id}
              data-testid={`history-${station.id}-item-${i}`}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-900/40"
            >
              <div className="w-10 h-10 shrink-0 rounded overflow-hidden border border-neutral-800 bg-black">
                {t.art ? (
                  <img src={t.art} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div
                    className="w-full h-full bg-cover bg-center"
                    style={{ backgroundImage: `url(${station.image})` }}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-body text-sm text-white truncate">{t.title}</div>
                <div className="font-body text-xs text-neutral-400 truncate">
                  {t.artist || "—"}
                </div>
              </div>
              <div className="font-condensed uppercase tracking-widest text-[10px] text-neutral-500 shrink-0">
                {formatTime(t.played_at)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const NowPlayingHistory = () => {
  const audioStations = STATIONS.filter((s) => s.kind !== "video");
  return (
    <section className="relative max-w-7xl mx-auto px-5 md:px-10 mt-24" data-testid="history-section">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 font-condensed uppercase tracking-[0.35em] text-[11px] text-gold">
            <History className="w-3.5 h-3.5" />
            Recently Played
          </div>
          <h2 className="mt-2 font-display text-4xl md:text-5xl uppercase text-white">
            Catch what you <span className="text-gold-gradient">missed</span>
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {audioStations.map((s) => (
          <HistoryColumn key={s.id} station={s} />
        ))}
      </div>
    </section>
  );
};

export default NowPlayingHistory;
